import * as React from 'react';
import {
  LiveClass,
  LiveClassCohort,
  LiveClassSession,
  ZoomUser
} from '@magoosh/admin/live_class_sessions/types';
import { useMemo, useState } from 'react';
import * as moment from 'moment-timezone';
import DropdownList from 'react-widgets/lib/DropdownList';
import fetch from 'utilities/fetch';
import { adminPaths } from 'config/path_helpers';
import { Col, Row } from 'react-bootstrap';
import { DefaultButton, LinkButton, PrimaryButton, SecondaryButton, TabButton } from '@magoosh/lib/button';
import { DataTable } from '@magoosh/lib/data_table';
import useDataQuery from '@magoosh/lib/data_table/use_data_query';
import { Form, Formik, FormikHelpers as FormikActions } from 'formik';
import { Search } from '@magoosh/lib/data_table/search';
import Modal from '@magoosh/lib/modal';
import LiveClassSessionForm from '@magoosh/admin/live_class_sessions/index/form';
import LiveClassSessionSyncForm from '@magoosh/admin/live_class_sessions/index/sync_form';
import { formatTime } from '@magoosh/admin/utils';
import LiveClassRegistrations from '@magoosh/admin/live_class_sessions/index/registrants';
import { QueryClient, QueryClientProvider } from 'react-query';

interface Props {
  cohortOptions: LiveClassCohort[];
  liveClasses: LiveClass[];
  zoomUsers: ZoomUser[];
}

const LiveClassSessionsIndex: React.FC<Props> = (props) => {
  const [syncing, setSyncing] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentRegistrantsSession, setCurrentRegistrantsSession] = useState(null);

  const openCreateModal = () => {
    setCurrentSession(null);
    setShowEditModal(true);
  };

  const openEditModal = (session: LiveClassSession) => {
    setCurrentSession(session);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
  };

  const closeSyncModal = () => {
    setSyncing(false);
    runQuery({ cohortId: 'none' });
  };

  const defaultQueryParams = useMemo(() => {
    const thoseParams = { sortColumn: 'startTime', filters: {} };
    return thoseParams;
  }, []);

  const { runQuery, dataQueryTableProps } = useDataQuery(
    adminPaths.api.liveClassSessions,
    defaultQueryParams
  );

  interface BulkUpdateValues {
    cohortId?: number;
    liveClassId?: number;
  }

  const initialBulkUpdateValues = {
    cohortId: null,
    liveClassId: null
  };

  const onSubmitBulkUpdate = (
    values: BulkUpdateValues,
    { setFieldError, setSubmitting }: FormikActions<BulkUpdateValues>
  ): void => {
    setSubmitting(true);
    fetch(adminPaths.api.liveClassSessionBulkUpdate(), {
      method: 'PATCH',
      body: JSON.stringify({
        ...values,
        liveClassSessionIds: dataQueryTableProps.selectedRows.map((row) => row.id)
      })
    })
      .then((response) => {
        runQuery();
      })
      .catch((response) => {
        alert(response);
      })
      .finally(() => setSubmitting(false));
  };

  const timeZone = moment.tz.guess();

  const zoomUsersOptions = props.zoomUsers.concat({ description: 'Any', zoomId: '', id: 'any' });

  return (
    <div>
      <div className="text-center">
        <TabButton disabled>Sessions</TabButton>
        <TabButton href={adminPaths.liveClassCohorts()}>Cohorts</TabButton>
        <TabButton href={adminPaths.liveClasses()}>Classes</TabButton>
      </div>
      <div>
        <h1>Sessions</h1>
        <p>Times are displayed in your local time zone: {timeZone}</p>
        {dataQueryTableProps.selectedRows.length > 0 && (
          <Row>
            <Formik onSubmit={onSubmitBulkUpdate} initialValues={initialBulkUpdateValues}>
              {({ errors, values, isSubmitting, submitForm, setFieldValue }) => (
                <Form>
                  <Col md={3}>
                    <DropdownList
                      name="cohortId"
                      data={props.cohortOptions.slice(0, -2)}
                      filter="contains"
                      placeholder="Select a Cohort"
                      textField="name"
                      valueField="id"
                      dataKey="id"
                      groupBy="attachableType"
                      onChange={(item) => setFieldValue('cohortId', item.id)}
                    />
                  </Col>
                  <Col md={3}>
                    <DropdownList
                      name="liveClassId"
                      filter="contains"
                      data={props.liveClasses}
                      placeholder="Select a Live Class"
                      textField="name"
                      valueField="id"
                      dataKey="id"
                      onChange={(item) => {
                        console.log(item.id);
                        setFieldValue('liveClassId', item.id);
                      }}
                    />
                  </Col>
                  <Col md={2}>
                    <PrimaryButton submitting={isSubmitting} onClick={submitForm}>
                      Update checked items
                    </PrimaryButton>
                  </Col>
                </Form>
              )}
            </Formik>
          </Row>
        )}
      </div>
      <DataTable
        {...dataQueryTableProps}
        selectableRows={true}
        columns={[
          {
            name: 'Name',
            selector: 'name',
            center: false,
            cell: (row) => (
              <div>
                <div>{row.name}</div>
                {row.liveClass && <small>{row.liveClass.name}</small>}
              </div>
            ),
            grow: 2
          },
          {
            name: 'Cohort',
            cell: (row) => <div>{row.cohort ? row.cohort.name : ''}</div>,
            selector: (row) => (row.cohort ? row.cohort.name : '')
          },
          {
            name: 'Start Time',
            selector: 'startTime',
            sortable: true,
            cell: (row) => {
              return <div>{row.startTime ? formatTime(row.startTime, timeZone) : ''}</div>;
            }
          },
          {
            name: 'Registrations',
            selector: 'registrations.length',
            right: true,
            cell: (row) => {
              return row.registrations.length > 0 ? (
                <LinkButton onClick={() => setCurrentRegistrantsSession(row)}>
                  {row.registrations.length}
                </LinkButton>
              ) : (
                0
              );
            }
          }
        ]}>
        <DataTable.HeaderActions>
          <PrimaryButton onClick={openCreateModal} icon="plus">
            Create
          </PrimaryButton>
          <SecondaryButton submitting={syncing} onClick={() => setSyncing(true)} icon="refresh">
            Sync
          </SecondaryButton>
        </DataTable.HeaderActions>
        <DataTable.Filters>
          <div className="filters" style={{ width: '800px' }}>
            <Row>
              <Col sm={4}>
                <Search runQuery={runQuery} placeholder={'Search Name...'} />
              </Col>
              <Col sm={5}>
                <DropdownList
                  filter="contains"
                  data={props.cohortOptions}
                  onChange={(selection) => {
                    runQuery({ cohortId: selection.id });
                  }}
                  placeholder="All Cohorts"
                  textField="name"
                  valueField="id"
                  dataKey="id"
                  groupBy="attachableType"
                />
              </Col>
              <Col sm={3}>
                <DropdownList
                  filter={'contains'}
                  data={zoomUsersOptions}
                  onChange={(selection) => {
                    runQuery({ zoomUserId: selection.id });
                  }}
                  placeholder="Zoom Users"
                  textField="description"
                  valueField="id"
                  dataKey="id"
                />
              </Col>
            </Row>
          </div>
        </DataTable.Filters>
        <DataTable.ItemActions>
          {(item) => (
            <DefaultButton icon="edit" onClick={() => openEditModal(item)}>
              Edit
            </DefaultButton>
          )}
        </DataTable.ItemActions>
      </DataTable>
      <Modal
        onHide={closeEditModal}
        show={showEditModal}
        title={currentSession !== null ? `Edit ${currentSession.name}` : 'New Session'}
        showCloseButton={true}>
        <div>
          <LiveClassSessionForm
            liveClassSession={currentSession}
            runQuery={runQuery}
            closeModal={closeEditModal}
          />
        </div>
      </Modal>
      <Modal
        onHide={closeSyncModal}
        show={syncing}
        title={'Sync from Zoom or GoToWebinar'}
        showCloseButton={true}>
        <LiveClassSessionSyncForm zoomUsers={props.zoomUsers} />
      </Modal>
      <Modal
        onHide={() => setCurrentRegistrantsSession(null)}
        show={!!currentRegistrantsSession}
        title={
          currentRegistrantsSession === null
            ? 'Registrations'
            : `Registrants for ${currentRegistrantsSession.name}`
        }
        showCloseButton={true}>
        <LiveClassRegistrations session={currentRegistrantsSession} />
      </Modal>
    </div>
  );
};

export default (props: Props) => {
  const queryClient = new QueryClient();

  return <QueryClientProvider client={queryClient}>
    <LiveClassSessionsIndex {...props} />
  </QueryClientProvider>;
};
